<script setup>
import { ref, computed, onActivated } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'

import AudienceService from '@/services/AudienceService'
import { useAudienceConsole } from '@/components/audience/audienceConsole'
import { viewerColor, viewerTag } from '@/components/audience/viewerIdentity'

import Spinner from '@/components/ui/Spinner.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconDownload from '@/components/icon/IconDownload.vue'

// The emails viewers left through the capture form, promoted from a cramped
// look-up dialog to a pane of its own. Deliberately not called "Captured
// emails": next to an "Email capture" tab that name reads as a duplicate.
// Email capture is where the form is configured; this is where what it
// collected lives.
//
// Erasure is per-email and per-stream — the GDPR lever a host owes anyone who
// asks — so it keeps its two-step confirm inline in the row. The pane (and
// its tab) stays even when capture is off: records outlive the feature, and
// export and erase have to stay reachable.

const { notify } = useNotification()

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	},
	streamAlive: {
		type: Boolean
	}
})

const {
	settings,
	usingCastr,
	emailCaptureOn,
	capturesTotal
} = useAudienceConsole()

const PAGE_SIZE = 50

const captures = ref([])
const total = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const loadError = ref('')
const exporting = ref(false)
const query = ref('')

// The row whose Erase is mid-confirm, and the one whose request is in flight.
const confirmingEmail = ref('')
const erasingEmail = ref('')

// Whether the form is currently collecting. The pane renders regardless —
// this only decides which explanation accompanies an empty list.
const collecting = computed(() => usingCastr.value && emailCaptureOn.value)

/** The list is the freshest count there is; keep the tab badge in step. */
const syncBadge = () => {
	capturesTotal.value = total.value
}

const load = async () => {
	loading.value = true
	loadError.value = ''
	confirmingEmail.value = ''

	try {
		const res = await AudienceService.listCaptures(props.stream._id, { limit: PAGE_SIZE, skip: 0 })
		captures.value = (res && res.captures) || []
		total.value = (res && res.total) || 0
		syncBadge()
	} catch (err) {
		loadError.value = err.message || 'Could not load the contacts'
	}

	loading.value = false
}

// Fires on first mount and on every return to this sub-tab (the shell keeps
// panes alive), so contacts that arrived while the host was elsewhere show up
// without a manual refresh. Reloading from the first page is the honest
// trade: "show more" progress resets, staleness does not accumulate.
onActivated(load)

const hasMore = computed(() => captures.value.length < total.value)

const loadMore = async () => {
	loadingMore.value = true

	try {
		const res = await AudienceService.listCaptures(props.stream._id, {
			limit: PAGE_SIZE,
			skip: captures.value.length
		})
		captures.value = captures.value.concat((res && res.captures) || [])
		total.value = (res && res.total) || total.value
		syncBadge()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not load more contacts' })
	}

	loadingMore.value = false
}

// Client-side, over what has been loaded: cheap and right for the common
// "find the address someone read out" case. The hint below the table says so
// when there are unloaded pages a match could be hiding in.
const filteredCaptures = computed(() => {
	const q = query.value.trim().toLowerCase()
	if (!q) return captures.value
	return captures.value.filter((c) =>
		(c.email || '').toLowerCase().includes(q) || (c.name || '').toLowerCase().includes(q))
})

// Date plus time, in the format the chat pane's day separators use. Relative
// times would suit the tab's live panes but go stale in a table nothing
// re-renders; the second-exact datetime stays a hover away on every cell.
const formatSeen = (ts) => {
	if (!ts) return '—'
	return new Date(ts).toLocaleString([], {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	})
}

// A single-capture contact has both stamps identical; repeating the same
// datetime across two columns reads as noise, so Last seen shows an em-dash.
const sameSeen = (capture) => {
	return !!capture.firstSeen && !!capture.lastSeen && capture.firstSeen === capture.lastSeen
}

const exactSeen = (capture) => {
	const first = capture.firstSeen ? new Date(capture.firstSeen).toLocaleString() : 'unknown'
	const last = capture.lastSeen ? new Date(capture.lastSeen).toLocaleString() : 'unknown'
	return `First seen ${first}, last seen ${last}`
}

/**
 * The export endpoint streams CSV, but it sits behind the same auth as every
 * other call, so a bare link would arrive without the auth-token header and
 * bounce. Fetch through the axios instance and hand the text to a Blob.
 */
const exportCsv = async () => {
	exporting.value = true

	try {
		const csv = await AudienceService.getCapturesCsv(props.stream._id)
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		const url = URL.createObjectURL(blob)

		const link = document.createElement('a')
		link.href = url
		link.download = `audience-captures-${props.stream.key || props.stream._id}.csv`
		link.click()
		URL.revokeObjectURL(url)
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not export the contacts' })
	}

	exporting.value = false
}

const erase = async (email) => {
	erasingEmail.value = email

	try {
		const res = await AudienceService.deleteCapture(props.stream._id, email)
		const deleted = (res && res.deleted) || 0

		captures.value = captures.value.filter((c) => c.email !== email)
		total.value = Math.max(0, total.value - Math.max(deleted, 1))
		confirmingEmail.value = ''
		syncBadge()

		notify({ group: 'success', text: `Erased ${email} from this stream` })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not erase the contact' })
	}

	erasingEmail.value = ''
}
</script>

<template>
	<section class="bg-surface-1 rounded-xl p-4 md:p-5">
		<div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-4">
			<div>
				<h4 class="text-base mb-1">Contacts</h4>
				<p class="text-sm text-surface-8 mb-0">
					Emails collected from viewers in the player. Erasing an address removes every capture
					of it from this stream — what to reach for when a viewer asks for their data to be
					deleted.
				</p>
			</div>
			<FormButton type="secondary" size="sm" :is-loading="exporting"
				:disabled="loading || !!loadError || !captures.length"
				title="Download every contact for this stream as CSV" @click="exportCsv">
				<IconBase class="!mr-1"><IconDownload /></IconBase>
				Export CSV
			</FormButton>
		</div>

		<div v-if="loading" class="py-10">
			<Spinner />
		</div>

		<template v-else-if="loadError">
			<Alert type="error" :text="loadError" />
			<div class="flex justify-center mt-3">
				<FormButton type="secondary" size="sm" @click="load">Try again</FormButton>
			</div>
		</template>

		<!-- Capture never ran (or everything was erased) and is not running
		now: point at where to switch it on instead of showing a bare empty
		table. The tab itself never hides — an invisible list was the
		discoverability complaint this layout exists to fix. -->
		<div v-else-if="!collecting && !captures.length" class="text-center py-8">
			<p class="text-sm text-surface-text-1 mb-1">
				{{ settings.enabled ? 'Email capture is switched off.' : 'Chat is switched off.' }}
			</p>
			<p class="text-tiny text-surface-7 mb-0">
				<template v-if="settings.enabled">
					Turn it on under
					<router-link :to="{ name: 'LivestreamManageAudienceEmailCapture' }">Email capture</router-link>
					and viewers who submit the form will appear here.
				</template>
				<template v-else>
					Turn it on with the switch above, then configure the form under
					<router-link :to="{ name: 'LivestreamManageAudienceEmailCapture' }">Email capture</router-link>.
				</template>
			</p>
		</div>

		<div v-else-if="!captures.length" class="text-center py-8">
			<p class="text-sm text-surface-text-1 mb-1">No contacts yet.</p>
			<p class="text-tiny text-surface-7 mb-0">
				Viewers who submit the email form in your player will appear here and in the CSV export.
			</p>
		</div>

		<template v-else>
			<Alert v-if="!collecting" type="warning" class="mb-3"
				text="Email capture is currently off. These contacts were collected earlier and stay available to export or erase." />

			<div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-3">
				<FormInput v-model="query" placeholder="Search by email or name"
					custom-classes="w-64 max-w-full" id="contacts-search" />
				<p class="text-tiny text-surface-7 mb-0">
					{{ total }} contact{{ total === 1 ? '' : 's' }} on this stream, newest first.
				</p>
			</div>

			<table class="w-full border-collapse">
				<thead>
					<tr class="text-left text-tiny text-surface-7 border-b border-surface-3">
						<th class="font-medium py-2 pr-3">Contact</th>
						<th class="font-medium py-2 pr-3 hidden sm:table-cell">Name</th>
						<th class="font-medium py-2 pr-3 hidden sm:table-cell">Consent</th>
						<th class="font-medium py-2 pr-3 hidden md:table-cell">First seen</th>
						<th class="font-medium py-2 pr-3 hidden md:table-cell">Last seen</th>
						<th class="py-2"><span class="sr-only">Actions</span></th>
					</tr>
				</thead>
				<tbody class="divide-y divide-surface-3">
					<template v-for="capture in filteredCaptures" :key="capture.email">
						<tr>
							<td class="py-2.5 pr-3">
								<div class="flex items-center gap-x-2.5 min-w-0">
									<!-- The same dot-and-tag a moderator sees against this
									viewer in chat, so an email can be matched to what its
									owner said without guessing. -->
									<span class="shrink-0 w-2.5 h-2.5 rounded-full"
										:style="{ backgroundColor: viewerColor(capture.vid) }"
										:title="capture.vid"></span>
									<div class="min-w-0">
										<p class="text-sm text-surface-text-1 truncate mb-0" :title="capture.email">
											{{ capture.email }}
										</p>
										<p class="text-tiny text-surface-7 truncate mb-0">
											<!-- Only below the breakpoint where Name has its
											own column. -->
											<span v-if="capture.name" class="sm:hidden">{{ capture.name }} · </span>
											<span v-if="capture.vid" class="font-mono">{{ viewerTag(capture.vid) }}</span>
											<!-- The narrow-viewport home of the seen columns. -->
											<span class="md:hidden" :title="exactSeen(capture)">
												<template v-if="capture.vid"> · </template>{{ formatSeen(capture.firstSeen) }}<template
													v-if="capture.lastSeen && capture.lastSeen !== capture.firstSeen">
													– {{ formatSeen(capture.lastSeen) }}</template>
											</span>
										</p>
									</div>
								</div>
							</td>
							<td class="py-2.5 pr-3 hidden sm:table-cell text-sm max-w-48"
								:class="capture.name ? 'text-surface-text-1' : 'text-surface-6'">
								<span class="block truncate" :title="capture.name || 'No name collected'">
									{{ capture.name || '—' }}
								</span>
							</td>
							<td class="py-2.5 pr-3 hidden sm:table-cell">
								<span class="text-tiny rounded px-1.5 py-0.5 whitespace-nowrap"
									:class="capture.consent
										? 'bg-success-transparent text-success'
										: 'bg-surface-3 text-surface-8'"
									:title="capture.consent
										? 'Ticked the consent box on the form'
										: 'Did not tick the consent box'">
									{{ capture.consent ? 'Consented' : 'No consent' }}
								</span>
							</td>
							<td class="py-2.5 pr-3 hidden md:table-cell text-tiny text-surface-7 whitespace-nowrap"
								:title="exactSeen(capture)">
								{{ formatSeen(capture.firstSeen) }}
							</td>
							<td class="py-2.5 pr-3 hidden md:table-cell text-tiny whitespace-nowrap"
								:class="sameSeen(capture) ? 'text-surface-6' : 'text-surface-7'"
								:title="sameSeen(capture) ? 'Same as first seen — captured once' : exactSeen(capture)">
								{{ sameSeen(capture) ? '—' : formatSeen(capture.lastSeen) }}
							</td>
							<td class="py-2.5 text-right">
								<FormButton v-if="confirmingEmail !== capture.email" type="secondary" size="sm"
									:disabled="!!erasingEmail" @click="confirmingEmail = capture.email">
									Erase
								</FormButton>
							</td>
						</tr>
						<!-- The confirm lives under its row, so the email being
						erased stays on screen while deciding. -->
						<tr v-if="confirmingEmail === capture.email" class="!border-t-0">
							<td colspan="6" class="pb-2.5">
								<div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 bg-surface-2 rounded-lg px-3 py-2">
									<p class="text-tiny text-surface-text-1 mb-0">
										Erase every capture of this email from this stream? It cannot be undone.
									</p>
									<div class="flex items-center gap-x-2">
										<FormButton type="secondary" size="sm" :disabled="erasingEmail === capture.email"
											@click="confirmingEmail = ''">
											Cancel
										</FormButton>
										<FormButton type="red" size="sm" :is-loading="erasingEmail === capture.email"
											@click="erase(capture.email)">
											Erase
										</FormButton>
									</div>
								</div>
							</td>
						</tr>
					</template>
				</tbody>
			</table>

			<p v-if="query && !filteredCaptures.length" class="text-tiny text-surface-7 text-center py-4 mb-0">
				No loaded contact matches "{{ query }}".
			</p>
			<p v-if="query && hasMore" class="text-tiny text-surface-7 mt-2 mb-0">
				Searching the {{ captures.length }} loaded of {{ total }} contacts — show more to widen it.
			</p>

			<div v-if="hasMore" class="flex justify-center mt-3">
				<FormButton type="secondary" size="sm" :is-loading="loadingMore" @click="loadMore">
					Show more ({{ captures.length }} of {{ total }})
				</FormButton>
			</div>
		</template>
	</section>
</template>
